import { __ } from "@wordpress/i18n";

export type StringReplacement =
	| `{{${string}}}`
	| `{{ ${string} }}`
	| `{{${string} }}`
	| `{{ ${string}}}`;

export type StringReplacementConfig = {
	[key in StringReplacement]?: string;
}[];

export function replaceStrings(text: string, obj: StringReplacementConfig) {
	return obj.reduce((acc, replacementObj) => {
		// biome-ignore lint/complexity/noForEach: Let's not mess with this.
		Object.entries(replacementObj).forEach(([key, value]) => {
			const regex = new RegExp(`{{\\s*${key.slice(2, -2).trim()}\\s*}}`, "g");
			// biome-ignore lint/style/noParameterAssign: Let's not mess with this.
			acc = acc.replace(regex, value ?? "");
		});
		return acc;
	}, text);
}

export const currentLanguage = window?.leat?.current_language || "en_US";

export function getTranslatedText(tx: Record<string, string> | null): string {
	if (!tx) return "";

	// Try to get the translation for the current language
	if (tx[currentLanguage]) {
		return tx[currentLanguage] ?? "";
	}

	// If not found, try to get the 'default' translation
	if (tx.default) {
		return tx.default;
	}

	// If 'default' is not available, fall back to the first available translation
	return tx[Object.keys(tx)[0] ?? ""] ?? "";
}

export function getSpendRuleLabel(
	text: string,
	credits: number | null,
	creditsName: string,
	discount: number | null,
	discountType: "percentage" | "fixed" | "free_shipping",
) {
	if (!text) return "";

	const get_discount_type = () => {
		if (discountType === "percentage") {
			return `${discount}%`;
		}

		if (discountType === "fixed") {
			const currency = window.leat.wcSettings.currency.symbol;

			return `${currency}${discount}`;
		}

		if (discountType === "free_shipping") {
			return __("Free Shipping", "leat-crm");
		}

		return `${discount}`;
	};

	return replaceStrings(text, [
		{
			"{{ credits_currency }}": creditsName ?? "",
			"{{ credits }}": credits?.toString() ?? "0",
			"{{ discount }}": get_discount_type(),
		},
	]);
}

/**
 * Get a translation from the plugin settings.
 * @param value - The value to get the translation from.
 * @param fallback - The fallback value to return if the translation is not found.
 * @returns The translation.
 */
export function getTranslation(value: unknown, fallback: string): string {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return getTranslatedText(value as Record<string, string>);
	}
	return fallback;
}
